import AppLink from "components/common/AppLink";
import If from "components/common/If";
import Loading from "components/common/loading/Loading";
import NoData from "components/common/NoData";
import ItemView, { ItemViewPropertyViewDataItemType, ItemViewPropsType } from "components/common/views/ItemView";
import { uuid } from "config/app-constants";
import { InternalDatalistTablePropsType } from "types/datalist-types";
import { resolveItemViewValue } from "utilities/datalist/datalist-sort-utilities";
import useAppContext from "utilities/hooks/useAppContext";
import DatalistRowActions from "./DatalistRowActions";
import { resolveBottomClassName } from "utilities/datalist/datalist-utilities";
import { isHidden } from "utilities/app-utilities";
import { ReactElement } from "react";

const DatalistCard = <TItemType, TFilterType, TSortType>(props: InternalDatalistTablePropsType<TItemType, TFilterType, TSortType>) => {
    let timer: any = null;
    const context = useAppContext();
    const renderConfig = typeof props.render === 'function' ? undefined : props.render;
    const legacyProps = props as any;
    const cardRenderer = renderConfig?.card ?? legacyProps.card;
    const footerCard = renderConfig?.footer?.card ?? legacyProps.footer?.card;
    const rowActions = renderConfig?.actions ?? legacyProps.actions;

    if (props.loading) {
        return (
            <div className="app-datalist_cards w-full">
                <div className={` w-full border theme-border px-4 py-2 mb-4 hover:border-sunrise-300`} >
                    <Loading />
                </div>
            </div>
        );
    }

    if (props.items.length === 0) return (
        <div className="app-datalist_cards w-full">
            <div className={` w-full border theme-border px-4 py-2 mb-4 hover:border-sunrise-300 text-center`} >
                <NoData />
            </div>
        </div>
    );

    const renderFooter = () => {
        if (footerCard) {
            let className = resolveBottomClassName(footerCard, 'sticky text-center w-full bg-neutral-50 dark:bg-neutral-900 p-2 shadow-md');
            return (
                <If condition={!!footerCard}>
                    <div className={className}>
                        {footerCard.element}
                    </div>
                </If>
            )
        } else {
            return <></>;
        }
    }

    if (cardRenderer) {
        return <div className="cards">
            {props.items.map((i, index) => {
                return cardRenderer(i, index);
            })}

            {renderFooter()}
        </div>
    }

    let definition: Record<string, InternalDatalistTablePropsType<TItemType, TFilterType, TSortType>['columns']> = {};

    const cols = [...props.columns].sort(function (a, b) { return !b.card?.groupOrder ? 0 : ((a.card?.groupOrder || 0) > (b.card?.groupOrder || 0) ? 1 : -1) });

    const defaultGroupName = uuid();
    for (let index = 0; index < cols.length; index++) {
        const column = cols[index];
        if (isHidden(column.hide) || isHidden(column.card?.hide)) continue;

        const name = column.card?.group || defaultGroupName;
        definition[name] = definition[name] || [];
        definition[name].push(column);
    }

    const getItemView = (dataItem: TItemType, index: number): ReactElement => {
        var properties: ItemViewPropsType['model'] = [];
        const nodeKey = `datalist-${props.name}-card-item-${props.itemId(dataItem)}`;
        for (const key in definition) {
            if (Object.prototype.hasOwnProperty.call(definition, key)) {
                const columns = definition[key];
                properties.push({
                    name: nodeKey + '-' + key,
                    items: columns.map(c => {
                        const resolve = c.card?.resolve || c.resolve;
                        let val = resolve ? resolve(dataItem, index, context) : resolveItemViewValue(c.name, dataItem, c.type)

                        let title = undefined;
                        if (c.card?.title) {
                            title = typeof c.card.title === 'boolean' ? (c.title || context.t(`fields.${c.name}.name`)) : c.card.title;
                        }

                        return {
                            name: c.name,
                            value: val,
                            icon: c.card?.icon,
                            textSize: c.card?.textSize,
                            label: title
                        } as ItemViewPropertyViewDataItemType
                    })
                })
            }
        };

        let actionsElement = <></>;
        if (rowActions) {
            const actions = typeof rowActions === 'function' ? rowActions(dataItem) : rowActions;
            if (actions) {
                const actionModel = [
                    {
                        name: props.name + "-cart-action",
                        items: [{
                            name: props.name + "-cart-action-items",
                            value: <DatalistRowActions
                                key={nodeKey + '-actions'}
                                {...actions}
                                itemId={props.itemId(dataItem)}
                                item={dataItem}
                                name={props.name + 'row-actions'}
                                onDelete={props.onDelete}
                                view='cart'
                            />
                        }]
                    }
                ];

                actionsElement = <ItemView view="compact" key={nodeKey + "-item-view-actions"} name={nodeKey} model={actionModel} />;
            }
        }

        let content = properties.length ? <>
            <ItemView className="grid grid-cols-3" view="compact" key={nodeKey + "-item-view-data"} name={nodeKey} model={properties} />
            {actionsElement}
        </> : <></>;

        if (props.onItemClick?.link) {
            content = <AppLink
                to={props.onItemClick.link.url}
                params={props.onItemClick.link.params && props.onItemClick.link.params(dataItem)}
                className="block"
            >
                {content}
            </AppLink>
        }

        return content;
    }



    const onClick = (item: TItemType, e: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (!props.onItemClick?.onClick) return;

        if (props.onItemClick?.type !== 'doubleClick') {
            props.onItemClick!.onClick!(item, e)
        }

        if (props.onItemClick?.type === 'doubleClick') {
            clearTimeout(timer);
            if (e.detail === 1) {
                timer = setTimeout(() => {
                }, 200);
            }
            else if (e.detail === 2) {
                props.onItemClick!.onClick!(item, e)
            }
        }
    }

    let tdPropsFn = (item: TItemType) => {
        let tdProps = {};
        if (props.onItemClick?.onClick) {
            switch (props.onItemClick.type) {
                case 'click':
                    tdProps = {
                        onClick: (e: React.MouseEvent<HTMLElement, MouseEvent>) => onClick(item, e)
                    };
                    break;
                case 'mouseUp':
                    tdProps = {
                        onMouseUp: (e: React.MouseEvent<HTMLElement, MouseEvent>) => props.onItemClick!.onClick!(item, e)
                    }
                    break;
                case 'mouseDown':
                    tdProps = {
                        onMouseDown: (e: React.MouseEvent<HTMLElement, MouseEvent>) => props.onItemClick!.onClick!(item, e)
                    }
                    break;
            }
        }
        return tdProps;
    }

    return (
        <div className="app-datalist_cards w-full">
            {props.items.map((dataItem, index) => {
                return <div
                    key={`datalist-${props.name}-card-item-${props.itemId(dataItem)}`}
                    className={` w-full border theme-border px-4 py-2 mb-4 hover:border-sunrise-300`}
                    {...tdPropsFn(dataItem)}
                >
                    {getItemView(dataItem, index)}
                </div>
            })}
            {renderFooter()}
        </div>
    );
};

export default DatalistCard;
